import { StrColumn } from './dataset.js';
import { setHtml } from './css.js';
import { parseNlQuery } from './nlquery.js';
import { parseFind, termMatcher } from './filters.js';
import { CHECKISH, replaceText, esc } from './grid-util.js';
export class GridFind {
    /** 바꿀 수 있는 셀 (보이는 행, scope 컬럼, 편집 가능, 체크·라디오 제외) 순회 */
    /** @internal */ replCells(col) {
        const out = [];
        const cis = this.cols.map((_c, i) => i).filter(i => !this.cols[i].special && !this.cols[i].def.value && (!col || this.cols[i].id === col));
        for (let vi = 0; vi < this.disp.length; vi++) {
            if (this.disp[vi] < 0)
                continue;
            for (const ci of cis)
                out.push({ vi, ci });
        }
        return out;
    }
    /** @internal */ replValue(vi, ci, find, repl, o) {
        const c = this.cols[ci], row = this.disp[vi], kind = this.editorOf(c, row);
        if (!kind || CHECKISH.has(kind) || !this.canEditCol(c, row))
            return null;
        const nt = replaceText(this.ds.get(row, c.id), find, repl, o);
        if (nt == null)
            return null;
        const p = kind === 'combo' ? this.parseCombo(c, nt) : this.parseInput(c, nt, kind, row);
        return 'error' in p ? null : p;
    }
    /** 모두 바꾸기 (편집 가능한 보이는 셀만, 실행 취소 한 번). 바꾼 셀 수 */
    replaceAll(find, repl, opts = {}) {
        this.commitEdit();
        const ch = [];
        for (const { vi, ci } of this.replCells(opts.col)) {
            const v = this.replValue(vi, ci, find, repl, opts);
            if (v)
                ch.push({ row: this.disp[vi], col: this.cols[ci].id, value: v.value });
        }
        if (ch.length)
            this.setCellByUser(ch, this.t('모두 바꾸기'), 'replace');
        return ch.length;
    }
    /** 다음 일치 셀로 이동 (값 문자열 기준, 바꿀 수 있는 셀만 — 일반 찾기는 Ctrl+F). 찾으면 true */
    findNextMatch(find, opts = {}) {
        const cells = this.replCells(opts.col);
        if (!cells.length || !find)
            return false;
        const cur = cells.findIndex(x => x.vi === this.focus.vi && x.ci === this.focus.ci);
        for (let k = 1; k <= cells.length; k++) {
            const { vi, ci } = cells[(cur + k + cells.length) % cells.length];
            if (this.replValue(vi, ci, find, '', opts)) {
                this.setFocus(vi, ci, false);
                return true;
            } // 바꿀 수 있는 셀만
        }
        return false;
    }
    /** 찾아 바꾸기 창 */
    openReplace() {
        this.replEl?.remove();
        const p = document.createElement('div');
        p.className = 'og-repl';
        p.setAttribute('role', 'dialog');
        p.setAttribute('aria-label', this.t('찾아 바꾸기'));
        const cur = this.cols[this.focus.ci];
        setHtml(p, `<div class="og-undohh"><b>${this.t('찾아 바꾸기')}</b><button type="button" data-a="x" aria-label="${this.t('닫기')}">×</button></div>
<label>${this.t('찾을 내용')} <input class="og-rf" type="text"></label><label>${this.t('바꿀 내용')} <input class="og-rr" type="text"></label>
<div class="og-ropt"><label><input class="og-rcol" type="checkbox"> ${this.t('현재 컬럼만')}${cur && !cur.special ? ` (${esc(cur.def.header ?? cur.id)})` : ''}</label><label><input class="og-rcase" type="checkbox"> ${this.t('대소문자')}</label><label><input class="og-rwhole" type="checkbox"> ${this.t('셀 전체 일치')}</label></div>
<div class="og-rmsg" role="status"></div><div class="og-undof"><button type="button" data-a="next">${this.t('다음 찾기')}</button> <button type="button" data-a="one">${this.t('바꾸기')}</button> <button type="button" data-a="all" class="og-rok">${this.t('모두 바꾸기')}</button></div>`);
        this.replEl = p;
        this.root.appendChild(p);
        const q = (s) => p.querySelector(s);
        const colId = cur && !cur.special ? cur.id : null;
        const opts = () => ({ col: q('.og-rcol').checked ? colId : null, matchCase: q('.og-rcase').checked, whole: q('.og-rwhole').checked });
        const msg = (t) => { p.querySelector('.og-rmsg').textContent = t; };
        const close = () => { p.remove(); this.replEl = null; this.focusGrid(); };
        const act = (a) => {
            const f = q('.og-rf').value, r = q('.og-rr').value;
            if (a === 'x')
                return close();
            if (!f) {
                msg(this.t('찾을 내용을 입력하세요'));
                return;
            }
            if (a === 'next') {
                msg(this.findNextMatch(f, opts()) ? '' : this.t('찾는 내용이 없습니다'));
                return;
            }
            if (a === 'one') {
                const v = this.replValue(this.focus.vi, this.focus.ci, f, r, opts());
                if (v && this.opts.columns.length) {
                    this.setCellByUser([{ row: this.disp[this.focus.vi], col: this.cols[this.focus.ci].id, value: v.value }], this.t('바꾸기'), 'replace');
                }
                msg(this.findNextMatch(f, opts()) ? (v ? '1건 바꿈' : '') : v ? '1건 바꿈 · 더 없음' : this.t('찾는 내용이 없습니다'));
                return;
            }
            const n = this.replaceAll(f, r, opts());
            msg(n ? `${n}건 바꿨습니다` : this.t('바꿀 내용이 없습니다'));
        };
        p.addEventListener('click', e => { const b = e.target.closest('[data-a]'); if (b)
            act(b.dataset.a); });
        p.addEventListener('keydown', e => { e.stopPropagation(); if (e.key === 'Escape')
            close();
        else if (e.key === 'Enter') {
            e.preventDefault();
            act('next');
        } });
        q('.og-rf').focus();
    }
    // ═══════════════════════ 찾기 패널 (Ctrl+F) ═══════════════════════
    openFind() {
        if (!this.findEl) {
            const f = document.createElement('div');
            f.className = 'og-find';
            setHtml(f, `<input type="search" data-f="q" placeholder="${esc(this.t('찾기: 단어 공백 = 모두 포함, +필수 -제외 "구문" 컬럼:값 김*수'))}" aria-label="${this.t('찾기')}">
<select data-f="mode" aria-label="${this.t('찾기 방식')}"><option value="search">${this.t('찾기 (강조)')}</option><option value="filter">${this.t('거르기')}</option><option value="nl">${this.t('자연어 질의')}</option></select><span class="og-nlx" aria-live="polite" hidden></span>
<span class="og-fcount" aria-live="polite"></span><button type="button" data-a="prev" title="${this.t('이전 (Shift+Enter)')}">↑</button><button type="button" data-a="next" title="${this.t('다음 (Enter, F3)')}">↓</button><button type="button" data-a="close" title="${this.t('닫기 (Esc)')}">✕</button>`);
            this.root.insertBefore(f, this.head);
            this.findEl = f;
            const q = f.querySelector('[data-f=q]');
            const mode = f.querySelector('[data-f=mode]');
            mode.value = this.findMode;
            let tm = 0;
            const nlx = f.querySelector('.og-nlx');
            // 자연어: 입력하는 동안 해석만 보여 주고, Enter 로 적용 (확인 후 적용)
            const nlPreview = () => { const r = this.parseNl(q.value); nlx.hidden = !q.value.trim(); nlx.className = 'og-nlx' + (r.unknown.length ? ' og-nlwarn' : ''); nlx.textContent = q.value.trim() ? this.t('해석') + ': ' + (r.explanation || this.t('(조건 없음)')) + (r.unknown.length ? ` · ${this.t('모르는 말')}: ${r.unknown.join(', ')}` : '') + ' — ' + this.t('Enter 로 적용') : ''; };
            q.addEventListener('input', () => { clearTimeout(tm); tm = window.setTimeout(() => (this.findMode === 'nl' ? nlPreview() : this.runFind(q.value)), 200); });
            mode.addEventListener('change', () => {
                const was = this.findMode;
                this.findMode = mode.value;
                q.placeholder = this.findMode === 'nl' ? this.t('예') + ': ' + this.t('내과 결과 140 이상 결과 높은 순') : this.t('찾기: 단어 공백 = 모두 포함, +필수 -제외 "구문" 컬럼:값 김*수');
                if (this.findMode === 'nl') {
                    if (was !== 'nl')
                        this.runFind('');
                    nlPreview();
                }
                else {
                    nlx.hidden = true;
                    if (was === 'nl')
                        this.clearNl();
                    this.runFind(q.value);
                }
            });
            q.addEventListener('keydown', e => {
                if (e.key === 'Enter' && this.findMode === 'nl') {
                    e.preventDefault();
                    clearTimeout(tm);
                    nlPreview();
                    const r = this.applyNlQuery(q.value);
                    nlx.textContent = (r.filters.length || r.sort.length ? this.t('적용') + ': ' + r.explanation : this.t('해석한 조건이 없습니다')) + (r.unknown.length ? ` · ${this.t('모르는 말')}: ${r.unknown.join(', ')}` : '');
                    return;
                }
                if (e.key === 'Enter') {
                    e.preventDefault();
                    clearTimeout(tm);
                    if (q.value !== this.findText)
                        this.runFind(q.value);
                    else
                        this.findNext(e.shiftKey ? -1 : 1);
                }
                else if (e.key === 'Escape') {
                    e.preventDefault();
                    this.closeFind();
                }
                else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    this.focusGrid();
                }
                else if (e.key === 'F3') {
                    e.preventDefault();
                    this.findNext(e.shiftKey ? -1 : 1);
                }
            });
            f.addEventListener('click', e => {
                const a = e.target.closest('button')?.dataset.a;
                if (a === 'next')
                    this.findNext(1);
                else if (a === 'prev')
                    this.findNext(-1);
                else if (a === 'close')
                    this.closeFind();
            });
            this.requestRender(true);
        }
        const q = this.findEl.querySelector('[data-f=q]');
        q.focus();
        q.select();
    }
    /** 자연어 해석 (적용하지 않음). 그리드에 보이는 컬럼만, 개인정보 컬럼 값은 사전에서 제외 */
    parseNl(text) {
        const cols = this.cols.filter(c => !c.special && !c.def.value).map(c => {
            const dc = this.ds.cols[c.id];
            const type = c.def.type === 'date' || c.def.format === 'date' || dc?.type === 'date' ? 'date' : dc?.kind === 'num' ? 'number' : 'string';
            const values = type === 'string' && !c.def.privacy && Array.isArray(dc?.dict) && dc.dict.length <= 5001 ? dc.dict.filter((v) => v != null) : undefined;
            return { id: c.id, header: String(c.def.header ?? c.id), type, values };
        });
        return parseNlQuery(text, cols, undefined, { t: this.t });
    }
    /** 자연어 질의 적용: 조건은 코드 필터(AND), 정렬은 정렬 키로. 해석 결과(설명·모르는 말)를 돌려줌 */
    applyNlQuery(text) {
        const r = this.parseNl(text);
        const cond = (f) => ({ kind: 'cond', col: f.col, op: f.op, value: f.value, ...(f.value2 !== undefined ? { value2: f.value2 } : {}) });
        this.setAdvancedFilter(r.filters.length ? { kind: 'group', logic: 'AND', items: r.filters.map(f => (f.or?.length ? { kind: 'group', logic: 'OR', items: [f, ...f.or].map(cond) } : cond(f))) } : null);
        if (r.sort.length)
            this.setSort(r.sort);
        this.nlApplied = true;
        this.opts.onNlQuery?.({ text, result: r, grid: this });
        return r;
    }
    /** @internal */ clearNl() { if (this.nlApplied) {
        this.nlApplied = false;
        this.setAdvancedFilter(null);
    } }
    closeFind() {
        if (this.findMode === 'nl')
            this.clearNl();
        this.findEl?.remove();
        this.findEl = null;
        const had = this.findText && this.findMode === 'filter';
        this.findText = '';
        this.findMatches = [];
        this.findIdx = -1;
        this.hits.clear();
        if (had)
            this.rebuild();
        else
            this.bump();
        this.requestRender(true);
        this.focusGrid();
    }
    /** 찾기 실행. filter 방식이면 행을 거르고, search 방식이면 일치 셀을 강조하고 첫 결과로 이동 */
    runFind(text) {
        this.findText = text.trim();
        this.hits.clear();
        this.findMatches = [];
        this.findIdx = -1;
        if (this.findMode === 'filter') {
            this.rebuild();
            this.updateFindCount();
            return;
        }
        this.rebuild();
        if (!this.findText) {
            this.updateFindCount();
            return;
        }
        const terms = parseFind(this.findText).filter(t => !t.neg);
        const neg = parseFind(this.findText).filter(t => t.neg);
        const rowOk = this.findRowTest([...parseFind(this.findText)]);
        const cols = this.findCols();
        const cellTests = terms.map(t => ({ t, m: termMatcher(t), cols: t.col ? cols.filter(c => c.id === this.findColId(t.col)) : cols }));
        for (let vi = 0; vi < this.disp.length && this.findMatches.length < 20000; vi++) {
            const row = this.disp[vi];
            if (row < 0 || !rowOk(row))
                continue;
            for (const c of cols) {
                const txt = this.displayText(c, this.valueOf(c, row), row);
                if (cellTests.some(ct => ct.cols.includes(c) && ct.m(txt))) {
                    this.findMatches.push({ row, col: c.id });
                    this.hits.add(row + ':' + c.id);
                }
            }
        }
        void neg;
        if (this.findMatches.length)
            this.findNext(1);
        else
            this.bump();
        this.updateFindCount();
    }
    findNext(dir) {
        if (!this.findMatches.length)
            return;
        this.findIdx = (this.findIdx + dir + this.findMatches.length) % this.findMatches.length;
        const m = this.findMatches[this.findIdx];
        const vi = this.disp.indexOf(m.row), ci = this.cols.findIndex(c => c.id === m.col);
        if (vi >= 0 && ci >= 0)
            this.setFocus(vi, ci, false);
        this.updateFindCount();
    }
    /** @internal */ updateFindCount() {
        const el = this.findEl?.querySelector('.og-fcount');
        if (!el)
            return;
        el.textContent = !this.findText ? '' : this.findMode === 'filter' ? `${this.view.length.toLocaleString('ko-KR')}행` : this.findMatches.length ? `${this.findIdx + 1} / ${this.findMatches.length.toLocaleString('ko-KR')}${this.findMatches.length >= 20000 ? '+' : ''}` : '없음';
    }
    /** @internal */ findCols() { return this.cols.filter(c => !c.special && !c.def.privacy); }
    /** @internal */ findColId(name) {
        const n = name.toLowerCase();
        return this.cols.find(c => c.id.toLowerCase() === n || (c.def.header ?? '').toLowerCase() === n)?.id ?? name;
    }
    /** 찾기 문법 → 행 판정. 문자열 컬럼은 사전 마스크로 빠르게 */
    /** @internal */ findRowTest(terms) {
        const cols = this.findCols();
        const tests = terms.map(t => {
            const m = termMatcher(t);
            const target = t.col ? cols.filter(c => c.id === this.findColId(t.col)) : cols;
            const fns = target.map(c => {
                const dc = this.ds.cols[c.id];
                if (dc instanceof StrColumn && !c.def.value) {
                    const mask = new Uint8Array(dc.dict.length);
                    for (let i = 1; i < dc.dict.length; i++)
                        mask[i] = m(this.displayText(c, dc.dict[i], -1)) || m(dc.dict[i] ?? '') ? 1 : 0;
                    return (r) => mask[dc.codes[r]] === 1;
                }
                return (r) => m(this.displayText(c, this.valueOf(c, r), r));
            });
            return { neg: t.neg, test: (r) => fns.some(f => f(r)) };
        });
        return r => tests.every(t => t.test(r) !== t.neg);
    }
    // ═══════════════════════ 증분 검색 ═══════════════════════
    /** @internal */ incEnabled() { return this.opts.incrementalSearch ?? !this.opts.editable; }
    /** @internal */ incInput(t) {
        this.incText += t;
        this.incSeek(0, 1);
    }
    /** 현재 컬럼 표시값이 incText 로 시작하는 행으로 이동. from: 0 = 현재 행부터, 1 = 다음 행부터 */
    /** @internal */ incSeek(from, dir) {
        clearTimeout(this.incTimer);
        this.incTimer = window.setTimeout(() => { this.incText = ''; this.hideTip(); }, 2500);
        const c = this.cols[this.focus.ci];
        if (!c || c.special) {
            this.incText = '';
            return;
        }
        const q = this.incText.toLowerCase();
        const n = this.disp.length;
        for (let k = 0; k < n; k++) {
            const vi = (this.focus.vi + (from + k) * dir + n * 2) % n;
            const row = this.disp[vi];
            if (row < 0)
                continue;
            if (this.displayText(c, this.valueOf(c, row), row).toLowerCase().startsWith(q)) {
                this.setFocus(vi, this.focus.ci, false);
                this.showTip('찾는 중: ' + this.incText);
                return;
            }
        }
        this.showTip('찾는 중: ' + this.incText + ' (없음)');
    }
    /** @internal */ handleIncKey(e) {
        if (e.key === 'Escape') {
            this.incText = '';
            this.hideTip();
            return true;
        }
        if (e.key === 'Backspace') {
            this.incText = this.incText.slice(0, -1);
            if (this.incText)
                this.incSeek(0, 1);
            else
                this.hideTip();
            return true;
        }
        if ((e.ctrlKey || e.metaKey) && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            this.incSeek(1, e.key === 'ArrowDown' ? 1 : -1);
            return true;
        }
        if (e.key.length > 1) {
            this.incText = '';
            this.hideTip();
        } // 다른 이동 키는 증분 검색 종료
        return false;
    }
}
